import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertTriangle, Eye, EyeOff, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import '../styles/pages/auth.css';

const LoginPage = () => {
  const { signIn, isLoggedIn, loading: authLoading } = useAuthContext();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });

  useEffect(() => {
    if (!authLoading && isLoggedIn) navigate('/dashboard', { replace: true });
  }, [authLoading, isLoggedIn, navigate]);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signIn(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__grid-bg" />
      <div className="auth-page__glow auth-page__glow--1" />
      <div className="auth-page__glow auth-page__glow--2" />

      <div className="auth-page__container">
        <Link to="/" className="auth-page__back">
          <ArrowLeft size={16} />
          Back to home
        </Link>

        <div className="auth-page__card">
          <div className="auth-page__header">
            <Link to="/" className="auth-page__logo">
              <img src="/logo.png" alt="AlphaNQ" className="auth-page__logo-img" />
            </Link>
            <h1 className="auth-page__title">Welcome back</h1>
            <p className="auth-page__desc">Sign in to access your live Nasdaq futures signals</p>
          </div>

          <form className="auth-page__form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-page__error">
                <AlertTriangle size={15} />
                <span>{error}</span>
              </div>
            )}

            <div className="auth-page__field">
              <label className="auth-page__label">Email address</label>
              <div className="auth-page__input-wrap">
                <Mail size={16} className="auth-page__input-icon" />
                <input
                  className="auth-page__input"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@example.com"
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="auth-page__field">
              <label className="auth-page__label">Password</label>
              <div className="auth-page__input-wrap">
                <Lock size={16} className="auth-page__input-icon" />
                <input
                  className="auth-page__input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="auth-page__toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-page__submit" disabled={loading}>
              {loading ? (
                <><span className="spinner" /> Signing in...</>
              ) : (
                <>Sign In <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <div className="auth-page__footer">
            Don&rsquo;t have an account?{' '}
            <Link to="/signup" className="auth-page__link">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
